import React, { useState } from 'react';
import { Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import {
  IconUser,
  IconMail,
  IconPhone,
  IconCreditCard,
  IconLock,
  IconCalendar,
  IconShieldCheck,
  IconId,
  IconRocket,
  IconMapPin,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { signupPayment } from '../../Users/services/UserServicesV2';
import { validateCPF, validateCNPJ, searchCEP } from '../../Customers/utils';
import TurnstileWidget from '../../../components/TurnstileWidget';
import { useTurnstile } from '../../../hooks/useTurnstile';

const PaymentStepForm = ({ planConfig, onPaymentSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [addressEnabled, setAddressEnabled] = useState(false);

  const { config: turnstileConfig, shouldShow: shouldShowTurnstile } = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      person_type: 2,
      customer_name: '',
      customer_email: '',
      customer_email_confirm: '',
      customer_phone: '',
      cpf: '',
      cnpj: '',
      card_holder_name: '',
      card_number: '',
      card_expiry: '',
      card_ccv: '',
      postal_code: '',
      address: '',
      address_number: '',
      complement: '',
      province: '',
      city: '',
      state: '',
    },
  });

  const personType = watch('person_type');

  const formatPhoneInput = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 6) v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    setValue('customer_phone', v, { shouldValidate: true });
  };

  const formatCardInput = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 19);
    setValue('card_number', v.replace(/(\d{4})/g, '$1 ').trim());
  };

  const formatExpiryInput = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue('card_expiry', v.length > 2 ? `${v.slice(0, 2)}/${v.slice(2)}` : v);
  };

  const formatCVVInput = (e) => setValue('card_ccv', e.target.value.replace(/\D/g, '').slice(0, 4));

  const handleCepLookup = async (rawCep) => {
    const cep = rawCep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await searchCEP(cep);
      if (res.success) {
        setValue('address', res.data.address || '');
        setValue('province', res.data.neighborhood || '');
        setValue('city', res.data.city || '');
        setValue('state', res.data.state || '');
        setAddressEnabled(true);
      } else {
        setAddressEnabled(true);
      }
    } catch {
      setAddressEnabled(true);
    } finally {
      setCepLoading(false);
    }
  };

  const formatCepInput = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 8);
    if (v.length > 5) v = `${v.slice(0, 5)}-${v.slice(5)}`;
    setValue('postal_code', v);
    if (v.replace(/\D/g, '').length === 8) handleCepLookup(v);
  };

  const onSubmit = async (data) => {
    if (shouldShowTurnstile && turnstileConfig?.enabled && !turnstileToken) {
      Swal.fire({ icon: 'warning', title: 'Verificação de Segurança',
        text: 'Complete a verificação antes de continuar.', confirmButtonColor: '#3b82f6' });
      return;
    }

    const cpfCnpj = data.person_type === 2
      ? (data.cpf || '').replace(/\D/g, '')
      : (data.cnpj || '').replace(/\D/g, '');

    const [month, cardYear] = (data.card_expiry || '').split('/');

    const payload = {
      plan_id: planConfig.plan_id,
      customer_name: data.customer_name.trim(),
      customer_email: data.customer_email.trim().toLowerCase(),
      customer_cpf_cnpj: cpfCnpj,
      customer_phone: data.customer_phone.replace(/\D/g, ''),
      card_holder_name: data.card_holder_name.trim(),
      card_number: data.card_number.replace(/\s/g, ''),
      card_expiry_month: month,
      card_expiry_year: cardYear,
      card_ccv: data.card_ccv,
      address: data.address.trim(),
      address_number: data.address_number.trim(),
      complement: (data.complement || '').trim(),
      province: data.province.trim(),
      postal_code: data.postal_code.replace(/\D/g, ''),
      city: (data.city || '').trim(),
      state: (data.state || '').trim().toUpperCase(),
    };
    if (turnstileToken) payload.turnstile_token = turnstileToken;

    setProcessing(true);
    try {
      const res = await signupPayment(payload);
      if (!res?.data?.success) throw new Error('Resposta inesperada do servidor');

      // FB pixel — Purchase
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: res.data.value || 0,
          currency: 'BRL',
          contents: [{ id: `plano-${planConfig.planName?.toLowerCase()}`, quantity: 1, item_price: res.data.value || 0 }],
          content_type: 'product',
        });
      }

      onPaymentSuccess({
        subscription_id: res.data.subscription_id,
        person_type: data.person_type,
        customer_name: data.customer_name.trim(),
        customer_email: data.customer_email.trim().toLowerCase(),
        customer_cpf_cnpj: cpfCnpj,
        customer_phone: data.customer_phone.replace(/\D/g, ''),
        plan: res.data.plan,
        next_due_date: res.data.next_due_date,
        value: res.data.value,
        card_brand: res.data.card_brand,
        card_last_digits: res.data.card_last_digits,
      });
    } catch (err) {
      const errData = err?.response?.data || {};
      const errCode = errData.error_code;

      if (errCode === 'ACCOUNT_ALREADY_EXISTS') {
        Swal.fire({
          icon: 'info',
          title: 'Conta já existe',
          text: 'Já existe uma conta com este e-mail/CPF. Faça login para contratar.',
          confirmButtonText: 'Ir para login',
          confirmButtonColor: '#3b82f6',
        }).then(() => { window.location.href = '/login'; });
      } else if (errCode === 'PAYMENT_DECLINED') {
        Swal.fire({
          icon: 'error',
          title: 'Pagamento recusado',
          text: 'A operadora do cartão não aprovou a cobrança. Verifique os dados ou use outro cartão.',
          confirmButtonColor: '#3b82f6',
        });
        if (shouldShowTurnstile) {
          setTurnstileResetTrigger((p) => p + 1);
          setTurnstileToken(null);
        }
      } else {
        let msg = 'Erro ao processar o pagamento. Verifique os dados do cartão e tente novamente.';
        if (errData?.error) msg = errData.error;
        else if (errData?.details) {
          const firstKey = Object.keys(errData.details)[0];
          if (firstKey) {
            const v = errData.details[firstKey];
            msg = Array.isArray(v) ? v[0] : (typeof v === 'string' ? v : msg);
          }
        }
        Swal.fire({ icon: 'error', title: 'Pagamento não aprovado', text: msg, confirmButtonColor: '#3b82f6' });
        if (shouldShowTurnstile) {
          setTurnstileResetTrigger((p) => p + 1);
          setTurnstileToken(null);
        }
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="signup-free-form">
      <div className="signup-free-form__card">
        <div className="signup-free-form__header">
          <div className={`signup-free-form__plan-badge signup-free-form__plan-badge--${planConfig.planBadgeVariant || 'primary'}`}>
            <IconRocket size={16} />
            Plano {planConfig.planName}
          </div>
          <p>Preencha os dados para concluir a contratação</p>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Tipo de Pessoa */}
          <Form.Group className="mb-3">
            <Form.Label><IconUser size={16} className="me-2" />Tipo de Pessoa</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check type="radio" name="person_type" id="pt_pf_paid"
                label="Pessoa Física (PF)" checked={personType === 2}
                onChange={() => setValue('person_type', 2)} disabled={processing} />
              <Form.Check type="radio" name="person_type" id="pt_pj_paid"
                label="Pessoa Jurídica (PJ)" checked={personType === 1}
                onChange={() => setValue('person_type', 1)} disabled={processing} />
            </div>
            <input type="hidden" {...register('person_type')} />
          </Form.Group>

          {/* Nome (PF=nome completo / PJ=razão social) */}
          <Form.Group className="mb-3">
            <Form.Label><IconUser size={16} className="me-2" />
              {personType === 1 ? 'Nome da Empresa' : 'Nome Completo'}
            </Form.Label>
            <Form.Control type="text"
              placeholder={personType === 1 ? 'Ex: Minha Empresa LTDA' : 'Ex: João Silva'}
              {...register('customer_name', {
                required: 'Obrigatório',
                maxLength: { value: 128, message: 'Máximo 128 caracteres' },
              })}
              disabled={processing} isInvalid={!!errors.customer_name} />
            {errors.customer_name && <Form.Control.Feedback type="invalid">{errors.customer_name.message}</Form.Control.Feedback>}
          </Form.Group>

          {/* CPF / CNPJ */}
          {personType === 2 ? (
            <Form.Group className="mb-3">
              <Form.Label><IconId size={16} className="me-2" />CPF</Form.Label>
              <NumberFormat
                className={`form-control ${errors.cpf ? 'is-invalid' : ''}`}
                format="###.###.###-##" mask="_" placeholder="000.000.000-00"
                onValueChange={(v) => setValue('cpf', v.value, { shouldValidate: true })}
                value={watch('cpf')} disabled={processing} />
              <input type="hidden" {...register('cpf', {
                required: personType === 2 ? 'CPF obrigatório' : false,
                validate: (v) => personType !== 2 || (v && validateCPF(v)) || 'CPF inválido',
              })} />
              {errors.cpf && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{errors.cpf.message}</Form.Control.Feedback>}
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label><IconId size={16} className="me-2" />CNPJ</Form.Label>
              <NumberFormat
                className={`form-control ${errors.cnpj ? 'is-invalid' : ''}`}
                format="##.###.###/####-##" mask="_" placeholder="00.000.000/0000-00"
                onValueChange={(v) => setValue('cnpj', v.value, { shouldValidate: true })}
                value={watch('cnpj')} disabled={processing} />
              <input type="hidden" {...register('cnpj', {
                required: personType === 1 ? 'CNPJ obrigatório' : false,
                validate: (v) => personType !== 1 || (v && validateCNPJ(v)) || 'CNPJ inválido',
              })} />
              {errors.cnpj && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{errors.cnpj.message}</Form.Control.Feedback>}
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><IconMail size={16} className="me-2" />E-mail</Form.Label>
                <Form.Control type="email" placeholder="seu@email.com"
                  {...register('customer_email', {
                    required: 'E-mail obrigatório',
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'E-mail inválido' },
                  })}
                  disabled={processing} isInvalid={!!errors.customer_email} />
                {errors.customer_email && <Form.Control.Feedback type="invalid">{errors.customer_email.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><IconMail size={16} className="me-2" />Confirmar e-mail</Form.Label>
                <Form.Control type="email" placeholder="Repita seu e-mail"
                  {...register('customer_email_confirm', {
                    required: 'Confirme o e-mail',
                    validate: (v) => v.toLowerCase() === watch('customer_email').toLowerCase() || 'Os e-mails não coincidem',
                  })}
                  onPaste={(e) => e.preventDefault()}
                  disabled={processing} isInvalid={!!errors.customer_email_confirm} />
                {errors.customer_email_confirm && <Form.Control.Feedback type="invalid">{errors.customer_email_confirm.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label><IconPhone size={16} className="me-2" />Telefone</Form.Label>
            <Form.Control type="text" placeholder="(11) 99999-9999"
              {...register('customer_phone', {
                required: 'Telefone obrigatório',
                validate: (v) => {
                  const d = v.replace(/\D/g, '');
                  return (d.length >= 10 && d.length <= 11) || 'Telefone inválido (DDD + número)';
                },
              })}
              onChange={formatPhoneInput} maxLength={15}
              disabled={processing} isInvalid={!!errors.customer_phone} />
            {errors.customer_phone && <Form.Control.Feedback type="invalid">{errors.customer_phone.message}</Form.Control.Feedback>}
          </Form.Group>

          {/* DADOS DO CARTÃO */}
          <hr className="my-4" />
          <h6 className="mb-3" style={{ color: '#0f172a' }}>
            <IconCreditCard size={18} className="me-2" />Dados do cartão
          </h6>

          <Form.Group className="mb-3">
            <Form.Label><IconUser size={16} className="me-2" />Nome impresso no cartão</Form.Label>
            <Form.Control type="text" placeholder="Como está no cartão"
              {...register('card_holder_name', { required: 'Obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
              disabled={processing} isInvalid={!!errors.card_holder_name} />
            {errors.card_holder_name && <Form.Control.Feedback type="invalid">{errors.card_holder_name.message}</Form.Control.Feedback>}
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><IconCreditCard size={16} className="me-2" />Número do cartão</Form.Label>
                <Form.Control type="text" placeholder="0000 0000 0000 0000"
                  {...register('card_number', { required: 'Obrigatório',
                    validate: (v) => v.replace(/\s/g, '').length >= 13 || 'Número de cartão inválido' })}
                  onChange={formatCardInput} maxLength={23}
                  disabled={processing} isInvalid={!!errors.card_number} />
                {errors.card_number && <Form.Control.Feedback type="invalid">{errors.card_number.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label><IconCalendar size={16} className="me-2" />Validade</Form.Label>
                <Form.Control type="text" placeholder="MM/AAAA"
                  {...register('card_expiry', {
                    required: 'Obrigatório',
                    pattern: { value: /^(0[1-9]|1[0-2])\/\d{4}$/, message: 'MM/AAAA' },
                  })}
                  onChange={formatExpiryInput} maxLength={7}
                  disabled={processing} isInvalid={!!errors.card_expiry} />
                {errors.card_expiry && <Form.Control.Feedback type="invalid">{errors.card_expiry.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label><IconLock size={16} className="me-2" />CVV</Form.Label>
                <Form.Control type="text" placeholder="000"
                  {...register('card_ccv', { required: 'Obrigatório',
                    minLength: { value: 3, message: '3-4 dígitos' }, maxLength: { value: 4, message: '3-4 dígitos' } })}
                  onChange={formatCVVInput} maxLength={4}
                  disabled={processing} isInvalid={!!errors.card_ccv} />
                {errors.card_ccv && <Form.Control.Feedback type="invalid">{errors.card_ccv.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          {/* ENDEREÇO */}
          <hr className="my-4" />
          <h6 className="mb-3" style={{ color: '#0f172a' }}>
            <IconMapPin size={18} className="me-2" />Endereço de cobrança
          </h6>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>CEP {cepLoading && <Spinner animation="border" size="sm" className="ms-2" />}</Form.Label>
                <Form.Control type="text" placeholder="00000-000"
                  {...register('postal_code', { required: 'Obrigatório',
                    pattern: { value: /^\d{5}-?\d{3}$/, message: 'CEP inválido' } })}
                  onChange={formatCepInput} maxLength={9}
                  disabled={processing || cepLoading} isInvalid={!!errors.postal_code} />
                {errors.postal_code && <Form.Control.Feedback type="invalid">{errors.postal_code.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Endereço</Form.Label>
                <Form.Control type="text" placeholder={addressEnabled ? 'Rua, Avenida...' : 'Preencha o CEP primeiro'}
                  {...register('address', { required: 'Obrigatório' })}
                  disabled={processing || !addressEnabled} isInvalid={!!errors.address} />
                {errors.address && <Form.Control.Feedback type="invalid">{errors.address.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Nº</Form.Label>
                <Form.Control type="text" placeholder="123"
                  {...register('address_number', { required: 'Obrigatório' })}
                  disabled={processing || !addressEnabled} isInvalid={!!errors.address_number} />
                {errors.address_number && <Form.Control.Feedback type="invalid">{errors.address_number.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Bairro</Form.Label>
                <Form.Control type="text"
                  {...register('province', { required: 'Obrigatório' })}
                  disabled={processing || !addressEnabled} isInvalid={!!errors.province} />
                {errors.province && <Form.Control.Feedback type="invalid">{errors.province.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Cidade</Form.Label>
                <Form.Control type="text" {...register('city')} disabled={processing || !addressEnabled} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>UF</Form.Label>
                <Form.Control type="text" maxLength={2}
                  {...register('state', { maxLength: { value: 2, message: '2 letras' } })}
                  disabled={processing || !addressEnabled} isInvalid={!!errors.state} />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Complemento (opcional)</Form.Label>
            <Form.Control type="text" placeholder="Apto, sala, bloco..."
              {...register('complement')} disabled={processing || !addressEnabled} />
          </Form.Group>

          {!addressEnabled && (
            <Alert variant="info" className="py-2">
              <small>Preencha o CEP para liberar os campos de endereço — será preenchido automaticamente.</small>
            </Alert>
          )}

          {/* Turnstile */}
          {shouldShowTurnstile && (
            <div className="mb-3">
              <TurnstileWidget
                onTokenChange={(t) => { setTurnstileToken(t); setTurnstileError(null); }}
                onError={(e) => { setTurnstileError(e); setTurnstileToken(null); }}
                onExpired={() => { setTurnstileToken(null); setTurnstileError('Token expirado, refaça.'); }}
                resetTrigger={turnstileResetTrigger}
              />
              {turnstileError && (
                <Alert variant="danger" className="mt-2"><IconShieldCheck size={16} className="me-2" />{turnstileError}</Alert>
              )}
            </div>
          )}

          <button type="submit"
            className="signup-plan-card__cta signup-plan-card__cta--success w-100"
            disabled={processing || (shouldShowTurnstile && !turnstileToken)}>
            {processing ? (
              <><Spinner animation="border" size="sm" className="me-2" />Processando pagamento...</>
            ) : (
              <><IconRocket size={20} className="me-2" />Confirmar Contratação</>
            )}
          </button>

          <div className="signup-free-form__secure">
            <IconShieldCheck size={14} />
            <span>Pagamento processado com criptografia SSL</span>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PaymentStepForm;
